import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal, Spinner } from "@heroui/react";
import { api, ScaledRecipe, ScaledRecipeBase, isSubrecipe, type ScaledIngredient, type NutritionData } from "../api/client";
import { useAuth } from "../auth";
import { formatUnit, MEAL_TYPE_LABELS } from "../constants";
import { Thumb } from "./Thumb";

interface Props {
  recipeId: number;
  servings: number;
  onClose: () => void;
  startCooking?: boolean;
}

export function RecipeModal({ recipeId, servings: initialServings, onClose, startCooking }: Props) {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [servings, setServings] = useState(Math.max(1, initialServings));
  const [recipe, setRecipe] = useState<ScaledRecipe | null>(null);
  const [favorite, setFavorite] = useState(false);
  const [currentSubrecipeId, setCurrentSubrecipeId] = useState<number | null>(null);
  const [cookingStep, setCookingStep] = useState<number | null>(startCooking ? 0 : null);

  useEffect(() => {
    setServings(Math.max(1, initialServings));
    setCurrentSubrecipeId(null);
    setCookingStep(startCooking ? 0 : null);
  }, [recipeId, initialServings, startCooking]);

  useEffect(() => {
    let cancelled = false;
    api
      .get<ScaledRecipe>(`/recipes/${recipeId}?servings=${servings}`)
      .then((r) => {
        if (!cancelled) {
          setRecipe(r);
          setFavorite(r.favorite);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [recipeId, servings]);

  async function toggleFavorite() {
    const next = !favorite;
    setFavorite(next); // optimista
    try {
      if (next) await api.post(`/recipes/${recipeId}/favorite`);
      else await api.del(`/recipes/${recipeId}/favorite`);
      window.dispatchEvent(new Event("cookmenu:data-changed"));
    } catch {
      setFavorite(!next); // reverteix si falla
    }
  }

  function editRecipe() {
    onClose();
    nav(`/receptes/${recipeId}/edita`);
  }

  const currentView: ScaledRecipeBase | null = useMemo(() => {
    if (!recipe) return null;
    if (currentSubrecipeId == null) return recipe;
    return recipe.subrecipes.find((s) => s.id === currentSubrecipeId) ?? recipe;
  }, [recipe, currentSubrecipeId]);

  const isCooking = cookingStep !== null && currentView && currentView.steps.length > 0;
  const inSubrecipe = currentSubrecipeId != null && recipe;

  function exitCooking() {
    setCookingStep(null);
  }

  function goBackToParent() {
    setCurrentSubrecipeId(null);
    setCookingStep(null);
  }

  function drillIntoSubrecipe(id: number) {
    setCurrentSubrecipeId(id);
    setCookingStep(null);
  }

  return (
    <Modal.Backdrop isOpen onOpenChange={(open) => !open && onClose()}>
      <Modal.Container>
        <Modal.Dialog className="sm:max-w-[640px]">
          <Modal.CloseTrigger />
          {!recipe || !currentView ? (
            <Modal.Body>
              <div className="flex items-center justify-center py-12">
                <Spinner />
              </div>
            </Modal.Body>
          ) : isCooking ? (
            <CookingMode
              recipe={currentView}
              step={cookingStep!}
              onStep={setCookingStep}
              onExit={exitCooking}
              servings={servings}
              onServings={setServings}
              canScale={currentSubrecipeId == null}
            />
          ) : (
            <RecipeView
              recipe={currentView}
              isSubrecipeView={!!inSubrecipe}
              parentName={inSubrecipe ? recipe.name : null}
              servings={servings}
              onServings={setServings}
              onStartCooking={() => setCookingStep(0)}
              subrecipes={inSubrecipe ? [] : recipe.subrecipes}
              onPickSubrecipe={drillIntoSubrecipe}
              onBack={goBackToParent}
              favorite={favorite}
              onToggleFavorite={toggleFavorite}
            />
          )}
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}

interface RecipeViewProps {
  recipe: ScaledRecipeBase;
  isSubrecipeView: boolean;
  parentName: string | null;
  servings: number;
  onServings: (n: number) => void;
  onStartCooking: () => void;
  subrecipes: ScaledRecipeBase[];
  onPickSubrecipe: (id: number) => void;
  onBack: () => void;
  favorite?: boolean;
  onToggleFavorite?: () => void;
}

function RecipeView({
  recipe,
  isSubrecipeView,
  parentName,
  servings,
  onServings,
  onStartCooking,
  subrecipes,
  onPickSubrecipe,
  onBack,
  favorite,
  onToggleFavorite,
}: RecipeViewProps) {
  const [activeTab, setActiveTab] = useState<"ingredients" | "steps" | "nutrition">("ingredients");
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const tabOrder: Array<"ingredients" | "steps" | "nutrition"> = recipe.steps.length > 0
    ? ["ingredients", "steps", "nutrition"]
    : ["ingredients", "nutrition"];

  function handleSwipe(dx: number) {
    const currentIdx = tabOrder.indexOf(activeTab);
    if (dx < -60) {
      const nextIdx = Math.min(currentIdx + 1, tabOrder.length - 1);
      setActiveTab(tabOrder[nextIdx]);
    } else if (dx > 60) {
      const prevIdx = Math.max(currentIdx - 1, 0);
      setActiveTab(tabOrder[prevIdx]);
    }
  }

  function onTouchStart(e: React.TouchEvent) {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  }

  function onTouchEnd(e: React.TouchEvent) {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      handleSwipe(dx);
    }
  }

  return (
    <>
      <Modal.Header className="flex flex-col gap-1.5 pt-2">
        {isSubrecipeView && parentName && (
          <button
            type="button"
            onClick={onBack}
            className="self-start text-xs text-muted hover:text-foreground"
          >
            ← Tornar a {parentName}
          </button>
        )}
        {/* Tags de tipus + temps */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="tag accent">{MEAL_TYPE_LABELS[recipe.mealType]}</span>
          {recipe.prepTimeMin && (
            <span className="inline-flex items-center gap-1 text-xs font-bold" style={{ color: "var(--muted)" }}>
              ⏱ {recipe.prepTimeMin} min
            </span>
          )}
        </div>
        {/* Títol + preferit */}
        <div className="flex flex-wrap items-center gap-2">
          {!isSubrecipeView && onToggleFavorite && (
            <button
              type="button"
              onClick={onToggleFavorite}
              aria-label={favorite ? "Treu de preferits" : "Marca preferida"}
              title={favorite ? "Treu de preferits" : "Marca preferida"}
              className="flex h-7 w-7 items-center justify-center transition-colors hover:opacity-70"
            >
              <span className="text-2xl leading-none">{favorite ? "★" : "☆"}</span>
            </button>
          )}
          <Modal.Heading className="text-2xl font-bold leading-tight tracking-tight">
            {recipe.name}
          </Modal.Heading>
        </div>
        {recipe.notes && (
          <p className="text-sm leading-relaxed text-muted">{recipe.notes}</p>
        )}
      </Modal.Header>

      <Modal.Body
        className="flex flex-col gap-3 max-h-[calc(100vh-200px)] overflow-y-auto overflow-x-hidden min-w-0"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* Imatge de la recepta */}
        <div
          className="overflow-hidden rounded-card-sm"
          style={{ height: 180, marginInline: "-0.5rem" }}
        >
          <Thumb
            name={recipe.name}
            imageUrl={recipe.imageUrl}
            meal={recipe.mealType}
            size={56}
            className="h-full w-full"
          />
        </div>

        {!isSubrecipeView && (
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted">Racions</span>
            <ServingsControl value={servings} onChange={onServings} min={1} />
          </div>
        )}
        {isSubrecipeView && (
          <p className="text-xs text-muted">
            Escalat a {recipe.effectiveServings} racions (heretat del pare).
          </p>
        )}

        <div className="flex gap-0.5 border-b border-default-200 -mx-4 px-4">
          <button
            type="button"
            onClick={() => setActiveTab("ingredients")}
            className={`px-3 py-2.5 text-sm font-semibold transition-colors relative ${
              activeTab === "ingredients"
                ? "text-foreground"
                : "text-muted hover:text-foreground"
            } ${activeTab === "ingredients" ? "after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-accent" : ""}`}
          >
            Ingredients
          </button>
          {recipe.steps.length > 0 && (
            <button
              type="button"
              onClick={() => setActiveTab("steps")}
              className={`px-3 py-2.5 text-sm font-semibold transition-colors relative ${
                activeTab === "steps"
                  ? "text-foreground"
                  : "text-muted hover:text-foreground"
              } ${activeTab === "steps" ? "after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-accent" : ""}`}
            >
              Pasos
            </button>
          )}
          <button
            type="button"
            onClick={() => setActiveTab("nutrition")}
            className={`px-3 py-2.5 text-sm font-semibold transition-colors relative ${
              activeTab === "nutrition"
                ? "text-foreground"
                : "text-muted hover:text-foreground"
            } ${activeTab === "nutrition" ? "after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-accent" : ""}`}
          >
            Informació nutricional
          </button>
        </div>

        <div>
          {activeTab === "ingredients" && (
            <div className="flex flex-col gap-6">
              <section>
                <ul className="flex flex-col text-[15px] leading-relaxed">
                  {recipe.ingredients.map((i) => (
                    <li
                      key={i.id}
                      className="flex items-baseline gap-3 border-b py-2.5 last:border-0"
                      style={{ borderColor: "var(--border)" }}
                    >
                      <span
                        className="shrink-0 font-bold tabular-nums"
                        style={{ minWidth: "4.5rem", color: "var(--accent-soft-foreground)" }}
                      >
                        {formatQuantity(i.quantity)} {formatUnit(i.unit, i.quantity)}
                      </span>
                      <span className="flex-1 font-semibold">
                        {i.name}
                        {i.note ? <span className="font-normal text-muted"> ({i.note})</span> : null}
                      </span>
                      <AddToShoppingButton
                        name={i.name}
                        quantity={i.quantity}
                        unit={i.unit}
                        ingredientId={i.ingredientId}
                      />
                    </li>
                  ))}
                </ul>
              </section>

              {subrecipes.length > 0 && (
                <section>
                  <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-muted">
                    Subreceptes
                  </h3>
                  <ul className="flex flex-col gap-2">
                    {subrecipes.map((sr) => (
                      <li key={sr.id}>
                        <button
                          type="button"
                          onClick={() => onPickSubrecipe(sr.id)}
                          className="flex w-full items-center justify-between gap-3 rounded-lg border border-default-200 bg-surface-secondary px-3 py-2 text-left transition-colors hover:bg-surface-tertiary"
                        >
                          <div className="flex flex-col">
                            <span className="text-sm font-semibold">{sr.name}</span>
                            <span className="text-xs text-muted">
                              {sr.ingredients.length} ingredients
                              {sr.steps.length > 0 ? ` · ${sr.steps.length} passos` : ""}
                            </span>
                          </div>
                          <span className="text-xs text-muted">veure →</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}

          {activeTab === "steps" && recipe.steps.length > 0 && (
            <section>
              <ol className="flex flex-col gap-3 text-[15px] leading-relaxed">
                {recipe.steps.map((s, idx) => (
                  <StepItem key={idx} index={idx + 1} text={s} />
                ))}
              </ol>
            </section>
          )}

          {activeTab === "nutrition" && (
            <NutritionSummary
              ingredients={recipe.ingredients}
              servings={recipe.effectiveServings}
            />
          )}
        </div>
      </Modal.Body>

      {recipe.steps.length > 0 && (
        <Modal.Footer className="border-t border-default-100 pt-4 lg:hidden">
          <Button fullWidth onPress={onStartCooking}>
            Comença a cuinar
          </Button>
        </Modal.Footer>
      )}
    </>
  );
}

interface CookingModeProps {
  recipe: ScaledRecipeBase;
  step: number;
  onStep: (n: number) => void;
  onExit: () => void;
  servings: number;
  onServings: (n: number) => void;
  canScale: boolean;
}

function CookingMode({ recipe, step, onStep, onExit, servings, onServings, canScale }: CookingModeProps) {
  const total = recipe.steps.length;
  const current = recipe.steps[step] ?? "";
  const colonIdx = current.indexOf(":");
  const hasTitle = colonIdx > 0 && colonIdx <= 60;
  const title = hasTitle ? current.slice(0, colonIdx) : null;
  const body = hasTitle ? current.slice(colonIdx + 1).trim() : current;
  const isLast = step === total - 1;

  const timers = useMemo(() => parseTimers(body), [body]);

  function goPrev() {
    if (step > 0) onStep(step - 1);
  }
  function goNext() {
    if (!isLast) onStep(step + 1);
  }

  // Canvi de pas amb el dit (swipe horitzontal).
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  function onTouchStart(e: React.TouchEvent) {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  }
  function onTouchEnd(e: React.TouchEvent) {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) goNext();
      else goPrev();
    }
  }

  return (
    <>
      <Modal.Header className="!pt-0">
        {/* -mt-2 alinea el títol amb els botons (diàleg p-6=24px vs botons top-4=16px). */}
        <div className="cook-head -mt-2 min-w-0 px-16 text-center">
          <p className="cook-name">{recipe.name}</p>
          <p className="cook-kicker">👨‍🍳 Cuina ara · pas {step + 1} de {total}</p>
        </div>
      </Modal.Header>

      <Modal.Body
        className="flex flex-col gap-5"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="cook-step">
          <span className="cook-num">{step + 1}</span>
          <div className="cook-step-body">
            {hasTitle && <h3 className="cook-title">{title}</h3>}
            <p className="cook-text">{body}</p>
          </div>
        </div>

        {timers.length > 0 && (
          <div className="flex flex-col gap-2">
            {timers.map((t, i) => (
              <CookTimer key={`${step}-${i}-${t.seconds}`} label={t.label} seconds={t.seconds} />
            ))}
          </div>
        )}

        <details className="cook-ing">
          <summary>
            <span>Ingredients</span>
            <span className="cook-ing-rac">
              {recipe.effectiveServings} {recipe.effectiveServings === 1 ? "ració" : "racions"}
            </span>
          </summary>
          {canScale && (
            <div className="cook-servings">
              <span className="text-sm text-muted">Racions</span>
              <ServingsControl value={servings} onChange={onServings} min={1} />
            </div>
          )}
          <ul>
            {recipe.ingredients.map((i) => (
              <li key={i.id}>
                <span className="cook-ing-q">
                  {formatQuantity(i.quantity)} {formatUnit(i.unit, i.quantity)}
                </span>{" "}
                {i.name}
              </li>
            ))}
          </ul>
        </details>
      </Modal.Body>

      <Modal.Footer className="cook-foot flex items-center gap-3 pt-4">
        {step === 0 ? (
          <Button variant="tertiary" onPress={onExit}>
            <ChevronIcon dir="left" />
            Surt
          </Button>
        ) : (
          <Button
            isIconOnly
            variant="tertiary"
            onPress={goPrev}
            aria-label="Pas anterior"
          >
            <ChevronIcon dir="left" />
          </Button>
        )}
        <div className="flex flex-1 flex-col gap-1.5">
          <div className="cook-dots">
            {recipe.steps.map((_, i) => (
              <span key={i} className={`cook-dot ${i <= step ? "on" : ""}`} />
            ))}
          </div>
        </div>
        {isLast ? (
          <Button onPress={onExit}>Fet ✓</Button>
        ) : (
          <Button isIconOnly onPress={goNext} aria-label="Pas següent">
            <ChevronIcon dir="right" />
          </Button>
        )}
      </Modal.Footer>

      <style>{`
        .cook-name{ font-family:var(--font-head); font-weight:600; font-size:15.5px; line-height:1.2;
          white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .cook-kicker{ margin-top:3px; font-size:11px; font-weight:800; text-transform:uppercase;
          letter-spacing:.13em; color:var(--accent-soft-foreground); }

        .cook-step{ display:flex; align-items:flex-start; gap:16px; padding:22px;
          border-radius:var(--radius-card);
          background:linear-gradient(152deg, var(--accent-soft),
            color-mix(in oklab, var(--accent-soft) 38%, var(--surface)));
          border:1px solid color-mix(in oklab, var(--accent) 16%, transparent); }
        .cook-num{ flex:0 0 auto; width:50px; height:50px; border-radius:50%; display:grid; place-items:center;
          background:var(--accent); color:var(--accent-foreground); font-family:var(--font-head);
          font-weight:700; font-size:23px; line-height:1;
          box-shadow:0 5px 14px color-mix(in oklab, var(--accent) 45%, transparent); }
        .cook-step-body{ flex:1; min-width:0; }
        .cook-title{ font-family:var(--font-head); font-weight:600; font-size:20px; line-height:1.18;
          margin-bottom:7px; color:var(--foreground); }
        .cook-text{ font-size:17px; line-height:1.55; color:var(--foreground); }

        .cook-ing{ border:1px solid var(--border); border-radius:var(--radius-card-sm);
          background:var(--surface-secondary); padding:13px 15px; }
        .cook-ing summary{ display:flex; align-items:center; justify-content:space-between;
          cursor:pointer; list-style:none; font-size:12px; font-weight:800; text-transform:uppercase;
          letter-spacing:.1em; color:var(--muted); }
        .cook-ing summary::-webkit-details-marker{ display:none; }
        .cook-ing-rac{ font-weight:700; letter-spacing:.02em; text-transform:none;
          color:var(--accent-soft-foreground); }
        .cook-servings{ display:flex; align-items:center; gap:12px; margin-top:12px;
          padding-bottom:12px; border-bottom:1px solid var(--border); }
        .cook-ing ul{ margin-top:11px; display:flex; flex-direction:column; gap:7px;
          font-size:14.5px; line-height:1.4; }
        .cook-ing li{ padding-bottom:7px; border-bottom:1px solid var(--border); }
        .cook-ing li:last-child{ padding-bottom:0; border-bottom:0; }
        .cook-ing-q{ font-weight:700; color:var(--accent-soft-foreground); }

        .cook-foot{ border-top:1px solid var(--border); }
        .cook-dots{ display:flex; gap:5px; }
        .cook-dot{ height:6px; flex:1; border-radius:99px; background:var(--surface-tertiary);
          transition:background .25s; }
        .cook-dot.on{ background:var(--accent); }
      `}</style>
    </>
  );
}

interface ServingsControlProps {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
}

function ServingsControl({ value, onChange, min = 1, max = 99 }: ServingsControlProps) {
  function step(delta: number) {
    const next = Math.max(min, Math.min(max, value + delta));
    if (next !== value) onChange(next);
  }
  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-default-200 bg-surface-secondary p-0.5">
      <button
        type="button"
        onClick={() => step(-1)}
        disabled={value <= min}
        aria-label="Una ració menys"
        className="flex h-7 w-7 items-center justify-center rounded-full text-sm text-foreground transition-colors hover:bg-surface disabled:opacity-30"
      >
        −
      </button>
      <span className="min-w-6 text-center text-sm font-semibold tabular-nums">{value}</span>
      <button
        type="button"
        onClick={() => step(1)}
        disabled={value >= max}
        aria-label="Una ració més"
        className="flex h-7 w-7 items-center justify-center rounded-full text-sm text-foreground transition-colors hover:bg-surface disabled:opacity-30"
      >
        +
      </button>
    </div>
  );
}

function ChevronIcon({ dir }: { dir: "left" | "right" }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {dir === "left" ? (
        <polyline points="15 18 9 12 15 6" />
      ) : (
        <polyline points="9 18 15 12 9 6" />
      )}
    </svg>
  );
}

function ActionIconButton({
  label,
  active = false,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={[
        "flex h-7 w-7 items-center justify-center rounded-lg transition-colors",
        active
          ? "bg-accent-soft text-accent-soft-foreground"
          : "text-muted hover:bg-default-hover hover:text-foreground",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

// Botó per afegir un ingredient (ja escalat a les racions actuals) a la llista
// de la compra. Mostra un check breu com a confirmació.
function AddToShoppingButton({
  name,
  quantity,
  unit,
  ingredientId,
}: {
  name: string;
  quantity: number;
  unit: string;
  ingredientId: number;
}) {
  const [state, setState] = useState<"idle" | "added">("idle");

  async function add() {
    try {
      await api.post("/shopping", {
        name,
        quantity: quantity > 0 ? quantity : null,
        unit: unit || null,
        ingredientId,
      });
      // Avisa la pantalla de la compra perquè es refresqui (pot haver-hi agregació).
      window.dispatchEvent(new CustomEvent("cookmenu:data-changed"));
      setState("added");
      setTimeout(() => setState("idle"), 1500);
    } catch {
      /* silenci: no bloquegem la vista de recepta */
    }
  }

  return (
    <button
      type="button"
      onClick={add}
      aria-label={`Afegeix ${name} a la llista de la compra`}
      title="Afegeix a la llista de la compra"
      className={[
        "flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors",
        state === "added"
          ? "text-success"
          : "text-muted hover:bg-default-hover hover:text-accent",
      ].join(" ")}
    >
      {state === "added" ? (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="8" cy="21" r="1" />
          <circle cx="19" cy="21" r="1" />
          <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
          <line x1="15" y1="6" x2="19" y2="6" />
          <line x1="17" y1="4" x2="17" y2="8" />
        </svg>
      )}
    </button>
  );
}

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function StepItem({ index, text }: { index: number; text: string }) {
  const colonIdx = text.indexOf(":");
  const hasTitle = colonIdx > 0 && colonIdx <= 60;
  const title = hasTitle ? text.slice(0, colonIdx) : null;
  const body = hasTitle ? text.slice(colonIdx + 1).trim() : text;

  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-soft text-xs font-bold text-accent-soft-foreground">
        {index}
      </span>
      <div className="flex-1">
        {hasTitle && <strong className="font-semibold text-foreground">{title}: </strong>}
        <span>{body}</span>
      </div>
    </li>
  );
}

function formatQuantity(q: number): string {
  if (Number.isInteger(q)) return String(q);
  const rounded = Math.round(q * 100) / 100;
  return rounded.toString();
}

interface ParsedTimer {
  label: string;
  seconds: number;
}

// Detecta expressions de temps dins el text d'un pas: "10 min", "1 h",
// "10-12 minuts", "1 hora 30 minuts", "45 segons" (català i castellà).
const TIME_RE =
  /(\d+)(?:\s*(?:[-–]|a|i|o)\s*(\d+))?\s*(hores|horas|hora|minuts|minutos|minut|minuto|min|segons|segundos|segon|segundo|seg|h)\b/gi;

function unitToSeconds(unit: string): number {
  const u = unit.toLowerCase();
  if (u.startsWith("h")) return 3600;
  if (u.startsWith("m")) return 60;
  return 1;
}

function humanizeSeconds(total: number): string {
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const parts: string[] = [];
  if (h) parts.push(`${h} h`);
  if (m) parts.push(`${m} min`);
  if (s) parts.push(`${s} s`);
  return parts.join(" ") || "0 s";
}

function parseTimers(text: string): ParsedTimer[] {
  const out: ParsedTimer[] = [];
  const seen = new Set<number>();
  for (const m of text.matchAll(TIME_RE)) {
    const n1 = parseInt(m[1], 10);
    const n2 = m[2] ? parseInt(m[2], 10) : null;
    // En un rang ("10-12 min") agafem el valor més alt, per no quedar-nos curts.
    const value = n2 != null ? Math.max(n1, n2) : n1;
    if (!value) continue;
    const seconds = value * unitToSeconds(m[3]);
    if (seconds <= 0 || seconds > 24 * 3600) continue;
    if (seen.has(seconds)) continue;
    seen.add(seconds);
    out.push({ label: humanizeSeconds(seconds), seconds });
  }
  return out;
}

function formatClock(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function notifyTimer(label: string) {
  if (!("Notification" in window)) return;
  if (Notification.permission === "granted") {
    new Notification("Temporitzador acabat", {
      body: `${label} ja està llest!`,
      icon: "/favicon.ico",
      badge: "/favicon.ico",
      tag: "timer-notification",
    });
  } else if (Notification.permission !== "denied") {
    Notification.requestPermission().then((permission) => {
      if (permission === "granted") {
        new Notification("Temporitzador acabat", {
          body: `${label} ja està llest!`,
          icon: "/favicon.ico",
          badge: "/favicon.ico",
          tag: "timer-notification",
        });
      }
    });
  }
}

function beep() {
  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    // Tres polsos curts.
    for (let i = 0; i < 3; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 880;
      const start = now + i * 0.4;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.3, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.25);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.3);
    }
    setTimeout(() => ctx.close(), 1500);
  } catch {
    /* àudio no disponible */
  }
  try {
    navigator.vibrate?.([200, 100, 200]);
  } catch {
    /* vibració no disponible */
  }
}

function PlayIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M4.5 3.2v9.6a.6.6 0 0 0 .92.5l7.4-4.8a.6.6 0 0 0 0-1l-7.4-4.8a.6.6 0 0 0-.92.5z" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <rect x="4" y="3" width="3" height="10" rx="1" />
      <rect x="9" y="3" width="3" height="10" rx="1" />
    </svg>
  );
}

function ReplayIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M13 8a5 5 0 1 1-1.46-3.54" />
      <path d="M13 2.5V5h-2.5" />
    </svg>
  );
}

function CookTimer({ label, seconds }: ParsedTimer) {
  const [remaining, setRemaining] = useState(seconds);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerIdRef = useRef<string>("");

  useEffect(() => {
    timerIdRef.current = Math.random().toString(36).slice(2);
  }, []);

  function clear() {
    if (intervalRef.current != null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    localStorage.removeItem(`timer:${timerIdRef.current}`);
  }

  useEffect(() => {
    const state = localStorage.getItem(`timer:${timerIdRef.current}`);
    if (!state) return;

    const { startTime, duration } = JSON.parse(state);
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    const newRemaining = Math.max(0, duration - elapsed);

    if (newRemaining <= 0) {
      setRemaining(0);
      setDone(true);
      setRunning(false);
      localStorage.removeItem(`timer:${timerIdRef.current}`);
      beep();
      notifyTimer(label);
    } else {
      setRemaining(newRemaining);
      setRunning(true);
    }
  }, [label]);

  useEffect(() => {
    if (!running) return;

    intervalRef.current = setInterval(() => {
      setRemaining((r) => {
        const newRemaining = r - 1;
        if (newRemaining <= 0) {
          clear();
          setRunning(false);
          setDone(true);
          beep();
          notifyTimer(label);
          return 0;
        }
        return newRemaining;
      });
    }, 1000);

    return clear;
  }, [running, label]);

  function start() {
    if (running) return;
    if (done || remaining <= 0) {
      setRemaining(seconds);
      setDone(false);
    }
    const newRemaining = done || remaining <= 0 ? seconds : remaining;
    setRunning(true);
    localStorage.setItem(
      `timer:${timerIdRef.current}`,
      JSON.stringify({ startTime: Date.now(), duration: newRemaining })
    );
  }

  function pause() {
    clear();
    setRunning(false);
  }

  function reset() {
    clear();
    setRunning(false);
    setDone(false);
    setRemaining(seconds);
  }

  const started = remaining < seconds || running || done;
  const urgent = running && remaining <= 10 && remaining > 0;
  const colorClass = done ? "text-success" : urgent ? "text-danger" : "text-accent";

  // Anella de progrés.
  const R = 26;
  const C = 2 * Math.PI * R;
  const progress = seconds > 0 ? remaining / seconds : 0;
  const dashOffset = C * (1 - progress);

  return (
    <div
      className={[
        "flex items-center gap-4 rounded-2xl border px-4 py-3 transition-colors",
        done
          ? "border-success/40 bg-success/10"
          : urgent
            ? "border-danger/40 bg-danger/5"
            : "border-default-200 bg-surface-secondary",
      ].join(" ")}
    >
      <div className={["relative shrink-0", colorClass, done ? "animate-pulse" : ""].join(" ")}>
        <svg width="64" height="64" viewBox="0 0 64 64" className="-rotate-90">
          <circle
            cx="32"
            cy="32"
            r={R}
            fill="none"
            strokeWidth="5"
            className="stroke-default-200"
          />
          <circle
            cx="32"
            cy="32"
            r={R}
            fill="none"
            strokeWidth="5"
            strokeLinecap="round"
            stroke="currentColor"
            strokeDasharray={C}
            strokeDashoffset={dashOffset}
            style={{ transition: "stroke-dashoffset 1s linear" }}
          />
        </svg>
        <span
          className={[
            "absolute inset-0 flex items-center justify-center text-sm font-bold tabular-nums",
            done ? "text-success" : "text-foreground",
          ].join(" ")}
        >
          {done ? "✓" : formatClock(remaining)}
        </span>
      </div>

      <div className="flex min-w-0 flex-col leading-tight">
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
          Temporitzador
        </span>
        <span className="truncate text-base font-semibold text-foreground">
          {done ? "Temps acabat!" : label}
        </span>
      </div>

      <div className="ml-auto flex items-center gap-1.5">
        {running ? (
          <Button isIconOnly size="sm" variant="tertiary" onPress={pause} aria-label="Pausa">
            <PauseIcon />
          </Button>
        ) : (
          <Button isIconOnly size="sm" onPress={start} aria-label={done ? "Repeteix" : "Inicia"}>
            {done ? <ReplayIcon /> : <PlayIcon />}
          </Button>
        )}
        {started && !running && !done && (
          <Button isIconOnly size="sm" variant="tertiary" onPress={reset} aria-label="Reinicia">
            <ReplayIcon />
          </Button>
        )}
      </div>
    </div>
  );
}

// Funcions auxiliars per nutrició
const UNIT_TO_GRAMS: Record<string, number> = {
  g: 1, kg: 1000, ml: 1, l: 1000, tsp: 5, tbsp: 15,
};

function toGrams(quantity: number, unit: string, gramsPerUnit?: number | null): number | null {
  const u = unit.toLowerCase();
  if (u === "u" || u === "unitat" || u === "unidat") {
    return gramsPerUnit ? quantity * gramsPerUnit : null;
  }
  const factor = UNIT_TO_GRAMS[u];
  return factor ? quantity * factor : null;
}

interface NutritionSummaryProps {
  ingredients: ScaledIngredient[];
  servings: number;
}

function NutritionSummary({ ingredients, servings }: NutritionSummaryProps) {
  const totals = useMemo(() => {
    const acc: Record<string, number> = {
      calories: 0,
      fat: 0,
      carbs: 0,
      protein: 0,
      fiber: 0,
      sugar: 0,
      sodium: 0,
    };

    let hasData = false;

    for (const ing of ingredients) {
      if (!ing.nutrition) continue;

      const grams = toGrams(ing.quantity, ing.unit, ing.gramsPerUnit);
      if (grams === null) continue;

      const factor = grams / 100;

      if (ing.nutrition.calories != null) {
        acc.calories += ing.nutrition.calories * factor;
        hasData = true;
      }
      if (ing.nutrition.fat != null) {
        acc.fat += ing.nutrition.fat * factor;
      }
      if (ing.nutrition.carbs != null) {
        acc.carbs += ing.nutrition.carbs * factor;
      }
      if (ing.nutrition.protein != null) {
        acc.protein += ing.nutrition.protein * factor;
      }
      if (ing.nutrition.fiber != null) {
        acc.fiber += ing.nutrition.fiber * factor;
      }
      if (ing.nutrition.sugar != null) {
        acc.sugar += ing.nutrition.sugar * factor;
      }
      if (ing.nutrition.sodium != null) {
        acc.sodium += ing.nutrition.sodium * factor;
      }
    }

    return hasData ? acc : null;
  }, [ingredients]);

  if (!totals) return null;

  const perServing = {
    calories: totals.calories / servings,
    fat: totals.fat / servings,
    carbs: totals.carbs / servings,
    protein: totals.protein / servings,
    fiber: totals.fiber / servings,
    sugar: totals.sugar / servings,
    sodium: totals.sodium / servings,
  };

  return (
    <section>
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-muted">
        Informació nutricional
        <span className="ml-1 font-normal normal-case">(per ració)</span>
      </h3>
      <div className="rounded-xl border border-default-200 bg-surface-secondary p-4 min-w-0">
        {/* Fila principal: Calories gran + macros */}
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-baseline sm:gap-3">
          <div className="flex flex-col shrink-0">
            <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">
              Calorías
            </span>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-foreground">
                {Math.round(perServing.calories)}
              </span>
              <span className="text-[10px] text-muted">kcal</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-4 flex-1 min-w-0">
            <div className="flex flex-col">
              <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">
                Proteína
              </span>
              <div className="flex items-baseline gap-0.5">
                <span className="text-lg font-semibold text-foreground">
                  {perServing.protein.toFixed(1)}
                </span>
                <span className="text-[10px] text-muted">g</span>
              </div>
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">
                Carbos
              </span>
              <div className="flex items-baseline gap-0.5">
                <span className="text-lg font-semibold text-foreground">
                  {perServing.carbs.toFixed(1)}
                </span>
                <span className="text-[10px] text-muted">g</span>
              </div>
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">
                Greix
              </span>
              <div className="flex items-baseline gap-0.5">
                <span className="text-lg font-semibold text-foreground">
                  {perServing.fat.toFixed(1)}
                </span>
                <span className="text-[10px] text-muted">g</span>
              </div>
            </div>
          </div>
        </div>

        {/* Fila secundaria: micros en text petit */}
        <div className="text-[12px] text-muted">
          <div className="space-y-1">
            {perServing.fiber > 0 && <div>🌾 Fibra: {perServing.fiber.toFixed(1)}g</div>}
            {perServing.sugar > 0 && <div>🍯 Sucres: {perServing.sugar.toFixed(1)}g</div>}
            {perServing.sodium > 0 && <div>🧂 Sodi: {Math.round(perServing.sodium)}mg</div>}
          </div>
        </div>
      </div>
    </section>
  );
}
